/**
 * Pagination Controls Component
 * Complete pagination UI with page navigation and size selector
 */

import React, { useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationEllipsis,
} from '@/components/ui/pagination';
import { PageSizeSelector } from './PageSizeSelector';
import { PaginationInfo } from './PaginationInfo';
import { getPaginationRange } from '@/lib/pagination/utils';
import { PaginationMeta } from '@/lib/pagination/types';

interface PaginationControlsProps {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  limitOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  showPageSize?: boolean;
  showInfo?: boolean;
  showFirstLast?: boolean;
  itemName?: string;
  className?: string;
  isLoading?: boolean;
}

export function PaginationControls({
  currentPage,
  pageSize,
  totalPages,
  totalCount,
  limitOptions = [10, 25, 50, 100],
  onPageChange,
  onPageSizeChange,
  showPageSize = true,
  showInfo = true,
  showFirstLast = true,
  itemName = 'items',
  className,
  isLoading = false,
}: PaginationControlsProps) {
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if no input is focused
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }

      if (e.key === 'ArrowLeft' && currentPage > 1) {
        e.preventDefault();
        onPageChange(currentPage - 1);
      } else if (e.key === 'ArrowRight' && currentPage < totalPages) {
        e.preventDefault();
        onPageChange(currentPage + 1);
      } else if (e.key === 'Home' && e.ctrlKey) {
        e.preventDefault();
        onPageChange(1);
      } else if (e.key === 'End' && e.ctrlKey) {
        e.preventDefault();
        onPageChange(totalPages);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, totalPages, onPageChange]);

  // Don't render if no data
  if (totalCount === 0) {
    return null;
  }

  const paginationRange = getPaginationRange(currentPage, totalPages);
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  return (
    <div
      className={cn(
        'flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between',
        className
      )}
    >
      {/* Info and page size selector */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        {showInfo && (
          <PaginationInfo
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={totalCount}
            itemName={itemName}
            className="text-sm text-muted-foreground"
          />
        )}
        {showPageSize && (
          <PageSizeSelector
            value={pageSize}
            options={limitOptions}
            onChange={onPageSizeChange}
            disabled={isLoading}
          />
        )}
      </div>

      {/* Pagination controls */}
      <Pagination>
        <PaginationContent>
          {/* First page button */}
          {showFirstLast && (
            <PaginationItem>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onPageChange(1)}
                disabled={!canGoPrevious || isLoading}
                aria-label="First page"
                className="hidden sm:inline-flex"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
            </PaginationItem>
          )}

          {/* Previous page button */}
          <PaginationItem>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={!canGoPrevious || isLoading}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </PaginationItem>

          {/* Page numbers */}
          {paginationRange.map((pageNumber, index) => {
            if (pageNumber === 'ellipsis') {
              return (
                <PaginationItem key={`ellipsis-${index}`} className="hidden sm:block">
                  <PaginationEllipsis />
                </PaginationItem>
              );
            }

            return (
              <PaginationItem key={pageNumber} className="hidden sm:block">
                <PaginationLink
                  onClick={() => onPageChange(pageNumber as number)}
                  isActive={currentPage === pageNumber}
                  className={cn('cursor-pointer', isLoading && 'pointer-events-none opacity-50')}
                >
                  {pageNumber}
                </PaginationLink>
              </PaginationItem>
            );
          })}

          {/* Mobile page indicator */}
          <PaginationItem className="sm:hidden">
            <span className="px-4 text-sm">
              {currentPage} / {totalPages}
            </span>
          </PaginationItem>

          {/* Next page button */}
          <PaginationItem>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={!canGoNext || isLoading}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </PaginationItem>

          {/* Last page button */}
          {showFirstLast && (
            <PaginationItem>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onPageChange(totalPages)}
                disabled={!canGoNext || isLoading}
                aria-label="Last page"
                className="hidden sm:inline-flex"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
    </div>
  );
}
